import React from 'react';
import { Tooltip, makeStyles, shorthands, tokens } from "@fluentui/react-components";

const useChartStyles = makeStyles({
    chartContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        ...shorthands.gap('10px'),
    },
    barsContainer: {
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.gap('20px'),
        ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
        paddingBottom: '10px',
        minHeight: '200px',
        alignItems: 'stretch',
    },
    groupContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.gap('5px'),
        flex: 1,
        height: '100%',
        justifyContent: 'flex-end',
    },
    barsGroup: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'center',
        ...shorthands.gap('4px'),
        flexGrow: 1,
        minHeight: '10px',
        width: '100%',
    },
    bar: {
        ...shorthands.borderRadius('2px'),
        cursor: 'pointer',
        transition: 'opacity 0.2s',
        ':hover': {
            opacity: 0.8,
        },
        minHeight: '4px',
        flex: 1,
        minWidth: '5px',
    },
    label: {
        fontSize: '10px',
        color: tokens.colorNeutralForeground2,
        maxWidth: '80px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    legendContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap('16px'),
        marginTop: '10px',
        justifyContent: 'center',
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('6px'),
        fontSize: '12px',
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        ...shorthands.padding('2px', '4px'),
        ...shorthands.borderRadius('4px'),
        transition: 'background-color 0.2s',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground3,
        },
    },
    legendColorBox: {
        width: '12px',
        height: '12px',
        ...shorthands.borderRadius('2px'),
    },
    tooltipContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.gap('4px'),
        padding: '4px',
    },
    tooltipValue: {
        fontSize: '18px',
        fontWeight: 'bold',
    },
    tooltipLabel: {
        fontSize: '12px',
        fontWeight: 600,
    },
    tooltipSubtext: {
        fontSize: '10px',
        color: tokens.colorNeutralForeground2,
    }
});

export interface IChartSeriesItem {
    key: string;
    data: number;
    legend: string;
    color: string;
    xAxisCalloutData?: string;
    yAxisCalloutData?: number;
    onClick?: () => void;
}

export interface IChartGroup {
    name: string;
    series: IChartSeriesItem[];
}

export interface SimpleGroupedBarChartProps {
    data: IChartGroup[];
    height?: number;
    barwidth?: number;
    chartTitle?: string;
    hideLabels?: boolean;
    chartLabel?: string;
    isCalloutForStack?: boolean;
    onLegendClick?: (legend: string, key: string) => void;
}

export const SimpleGroupedBarChart: React.FC<SimpleGroupedBarChartProps> = ({ 
    data, 
    height = 300, 
    barwidth = 20, 
    chartTitle,
    onLegendClick
}) => {
    const styles = useChartStyles();
    
    if (!data || data.length === 0) {
        return <div>No data available</div>;
    }

    // Find max value for scaling
    let maxValue = 0;
    data.forEach((group) => {
        group.series.forEach((item) => {
            if (item.data > maxValue) maxValue = item.data;
        });
    });
    
    if (maxValue === 0) maxValue = 10;
    else maxValue = maxValue * 1.1;

    // Extract unique legend items with their keys
    const uniqueLegends = Array.from(
        new Map(
            data.flatMap(g => g.series.map(s => [s.legend, { color: s.color, key: s.key }]))
        ).entries()
    ).map(([legend, { color, key }]) => ({ legend, color, key }));

    const handleLegendClick = (legend: string, key: string) => {
        if (onLegendClick) {
            onLegendClick(legend, key);
        }
    };

    return (
        <div className={styles.chartContainer}>
            {chartTitle && <div style={{ fontWeight: 600 }}>{chartTitle}</div>}
            
            {/* Legend Section */}
            <div className={styles.legendContainer}>
                {uniqueLegends.map((item) => (
                    <div 
                        key={item.legend} 
                        className={styles.legendItem}
                        onClick={() => handleLegendClick(item.legend, item.key)}
                        title={onLegendClick ? `Click to filter by ${item.legend}` : ''}
                        {...(onLegendClick ? { role: "button", "aria-label": `Filter by ${item.legend}`, tabIndex: 0 } : {})}
                        onKeyDown={(e) => {
                            if (onLegendClick && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                handleLegendClick(item.legend, item.key);
                            }
                        }}
                    >
                        <div 
                            className={styles.legendColorBox} 
                            style={{ backgroundColor: item.color }} 
                        />
                        <span>{item.legend}</span>
                    </div>
                ))}
            </div>

            <div className={styles.barsContainer} style={{ height: height }}>
                {data.map((group, groupIndex) => (
                    <div key={groupIndex} className={styles.groupContainer}>
                        <div className={styles.barsGroup}>
                            {group.series.map((item, itemIndex) => {
                                const barHeight = (item.data / maxValue) * 100;
                                return (
                                    <Tooltip 
                                        key={itemIndex} 
                                        content={
                                            <div className={styles.tooltipContainer}>
                                                <span className={styles.tooltipLabel} style={{ color: item.color }}>{item.legend}</span>
                                                <span className={styles.tooltipValue}>{item.yAxisCalloutData ?? item.data}</span>
                                                <span className={styles.tooltipSubtext}>{group.name}</span>
                                            </div>
                                        }
                                        relationship="label"
                                    >
                                        <div
                                            className={styles.bar}
                                            style={{
                                                height: `${barHeight}%`,
                                                backgroundColor: item.color
                                            }}
                                            onClick={item.onClick}
                                            role="button"
                                            aria-label={`${item.legend}: ${item.data}`}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </div>
                        <div className={styles.label} title={group.name}>{group.name}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};
